import { useCallback, useEffect, useRef, useState } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { EcommerceResponse, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import ProductCard from './ProductCard.tsx'
import style from './FeaturedCarousel.module.scss'

interface IFeaturedCarouselProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  products: IEcommerceAnswerSchema[]
  onSelect: (index: number) => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
}

type Arrows = { prev: boolean; next: boolean }

const readArrows = (el: HTMLDivElement): Arrows => ({
  prev: el.scrollLeft > 1,
  next: el.scrollLeft + el.clientWidth < el.scrollWidth - 1
})

// "Usually bought next" on the detail page: featured products as grid cards in a scrolling row.
const FeaturedCarousel = ({ schema, response, products, onSelect, onAddToCart }: IFeaturedCarouselProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const trackRef = useRef<HTMLDivElement>(null)
  const [arrows, setArrows] = useState<Arrows>({ prev: false, next: false })

  // Identity-preserving: the booleans change at the ends only, so most scroll frames bail out.
  const updateArrows = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    const next = readArrows(el)
    setArrows(prev => (prev.prev === next.prev && prev.next === next.next ? prev : next))
  }, [])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    let frame = 0
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        updateArrows()
      })
    }
    schedule()
    if ('undefined' === typeof ResizeObserver) return () => cancelAnimationFrame(frame)
    const observer = new ResizeObserver(schedule)
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [updateArrows, products.length])

  const scrollPage = (direction: 1 | -1) => {
    const el = trackRef.current
    if (!el) return
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' })
  }

  return (
    // The app's row is swiped, so it has no arrows and no count.
    <section
      className={`${style.carousel} ${viewport.mobile ? style.mobile : ''}`}
      data-testid="SW-EC-FeaturedCarousel"
    >
      <div className={style.header}>
        <h2 className={style.title}>{t('questions', 'ec', 'tesco', 'usuallyBoughtNext')}</h2>
        {!viewport.mobile && (
          <span className={style.count}>
            {t<(count: number) => string>('questions', 'ec', 'tesco', 'itemsCount')(products.length)}
          </span>
        )}
      </div>
      <div className={style.viewport}>
        <div className={style.track} ref={trackRef} onScroll={updateArrows}>
          {products.map(item => (
            <div className={style.slide} key={item.id}>
              <ProductCard
                schema={schema}
                answer={item}
                amount={response.answers[item.index].v}
                disabled={'focus' == schema.mode_type && !item.focused}
                bannerPlacement="featured"
                onSelect={() => onSelect(item.index)}
                onAdd={qnt => onAddToCart(item.index, qnt, true)}
              />
            </div>
          ))}
        </div>
        {!viewport.mobile && arrows.prev && (
          <div className={`${style.scrim} ${style.prev}`}>
            <button
              type="button"
              className={style.arrow}
              onClick={() => scrollPage(-1)}
              aria-label={t('questions', 'ec', 'tesco', 'previous')}
            >
              <i className="pi pi-chevron-left" />
            </button>
          </div>
        )}
        {!viewport.mobile && arrows.next && (
          <div className={`${style.scrim} ${style.next}`}>
            <button
              type="button"
              className={style.arrow}
              onClick={() => scrollPage(1)}
              aria-label={t('questions', 'ec', 'tesco', 'next')}
            >
              <i className="pi pi-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default FeaturedCarousel
